// react-query hooks over api/botApi.ts. All query keys are stable arrays
// under the ['bot', …] prefix. Async completion (poll cadence, badge/toast)
// is split between two levels on purpose:
//   - useBotConversation: polls the whole open conversation (BotPanel's job
//     while the panel is showing it).
//   - useBotTurn: polls a single turn (BotWidget's <TurnWatcher/>, used for
//     turns the panel isn't currently displaying — see its own `poll` note).

import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  askTurn,
  cancelAnswer,
  createConversation,
  deleteMemory,
  getConversation,
  getMemory,
  getModelCatalog,
  getModels,
  getOrgSettings,
  getSettings,
  getTurn,
  getMonthUsage,
  getUsageReport,
  listConversations,
  patchConversation,
  patchModel,
  patchOrgSettings,
  patchSettings,
  syncModels,
  type BotConversationDetail,
  type BotTurn,
  type ConversationCreateRequest,
  type ConversationPatchRequest,
  type OrgSettingsPatchRequest,
  type SettingsPatchRequest,
  type TurnCreateRequest,
} from '../api/botApi'
import type { BotCatalogModel } from '../api/botApi'
import { useBotStore } from '../botStore'
import { anyNonTerminal, conversationHasNonTerminal } from '../botUtils'

// ── Models ───────────────────────────────────────────────────────────────────

export function useBotModels() {
  return useQuery({
    queryKey: ['bot', 'models'],
    queryFn: getModels,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * IT.Admin-only model catalog (enabled + disabled rows). `enabled` is the
 * caller's gate — SettingsTab passes `isAdmin && <catalog section mounted>`
 * so the request can never fire for a non-admin, matching useBotSettings'
 * `opts.enabled` pattern above.
 */
export function useModelCatalog(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['bot', 'models', 'catalog'],
    queryFn: getModelCatalog,
    staleTime: 30_000,
    enabled: opts?.enabled ?? true,
  })
}

/** POST /api/bot/models/sync — invalidates both the enabled-only picker list
 *  and the admin catalog so a newly-added (disabled) model shows up in the
 *  catalog immediately without waiting on staleTime. */
export function useSyncModels() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => syncModels(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bot', 'models'] })
      qc.invalidateQueries({ queryKey: ['bot', 'models', 'catalog'] })
    },
  })
}

/** PATCH /api/bot/models/{id} — OPTIMISTIC: the toggle flips in the cached
 *  catalog immediately (owner report 2026-09-01: waiting for the refetch —
 *  on a cold Function up to ~30s — made the whole list feel dead), rolls
 *  back on error, and reconciles with the server on settle. */
export function useToggleModel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, isEnabled }: { id: number; isEnabled: boolean }) => patchModel(id, { isEnabled }),
    onMutate: async ({ id, isEnabled }) => {
      await qc.cancelQueries({ queryKey: ['bot', 'models', 'catalog'] })
      const previous = qc.getQueryData<BotCatalogModel[]>(['bot', 'models', 'catalog'])
      qc.setQueryData<BotCatalogModel[]>(['bot', 'models', 'catalog'], (rows) =>
        rows?.map((m) => (m.id === id ? { ...m, isEnabled } : m)),
      )
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(['bot', 'models', 'catalog'], ctx.previous)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['bot', 'models'] })
      qc.invalidateQueries({ queryKey: ['bot', 'models', 'catalog'] })
    },
  })
}

// ── Conversations ────────────────────────────────────────────────────────────

export function useBotConversations(opts?: { archived?: boolean; enabled?: boolean }) {
  const archived = opts?.archived ?? false
  return useQuery({
    queryKey: ['bot', 'conversations', archived],
    queryFn: () => listConversations({ archived }),
    enabled: opts?.enabled ?? true,
  })
}

export function useBotConversation(id: number | null | undefined) {
  const qc = useQueryClient()
  // Gated on the panel's own isOpen too (not just `id != null`) — BotPanel
  // is always mounted (only its AnimatePresence content is conditional on
  // isOpen), so without this the conversation poll would keep hitting
  // GET /conversations/{id} every 1.5s even with the panel closed, doubling
  // up with TurnWatcher's own poll of the same turns.
  const isOpen = useBotStore((s) => s.isOpen)
  const result = useQuery({
    queryKey: ['bot', 'conversation', id],
    queryFn: () => getConversation(id as number),
    enabled: id != null && isOpen,
    refetchInterval: (query) => {
      if (query.state.status === 'error') return false
      const data = query.state.data as BotConversationDetail | undefined
      return conversationHasNonTerminal(data) ? 1500 : false
    },
    refetchIntervalInBackground: false,
  })

  // Mirror every turn this (fresher) poll carries into its own
  // ['bot','turn',turnId] cache entry, so TurnWatcher's useBotTurn — which
  // suspends its OWN polling while this conversation is the one on screen —
  // still observes completion instead of being stuck on the snapshot it
  // fetched at mount.
  //
  // Guarded against clobbering a fresher single-turn fetch: TurnWatcher
  // keeps polling ['bot','turn',turnId] itself until it learns (from a
  // previous mirror write, or its own first fetch) that this conversation
  // covers it, so its poll can still resolve *after* this conversation
  // fetch started but *before* it lands here. Only mirror when this fetch is
  // at least as fresh as whatever is already cached for that turn.
  useEffect(() => {
    if (!result.data) return
    const conversationFetchedAt = result.dataUpdatedAt
    for (const turn of result.data.turns) {
      const turnKey = ['bot', 'turn', turn.turnId]
      const existing = qc.getQueryState(turnKey)
      if (existing && existing.dataUpdatedAt > conversationFetchedAt) continue
      const nextTurn = {
        turnId: turn.turnId,
        conversationId: turn.conversationId,
        question: turn.question,
        askedAt: turn.askedAt,
        answers: turn.answers,
      }
      // Skip the write when nothing actually changed — without this, every
      // poll tick rewrites every turn's cache entry (even a fully "done"
      // one, which will never change again), bumping its dataUpdatedAt and
      // re-rendering every mounted useBotTurn consumer for no reason.
      if (JSON.stringify(qc.getQueryData(turnKey)) === JSON.stringify(nextTurn)) continue
      qc.setQueryData(turnKey, nextTurn)
    }
  }, [result.data, result.dataUpdatedAt, qc])

  return result
}

export function useCreateConversation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ConversationCreateRequest) => createConversation(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bot', 'conversations'] }),
  })
}

export function usePatchConversation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: ConversationPatchRequest }) => patchConversation(id, body),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ['bot', 'conversation', vars.id] })
      qc.invalidateQueries({ queryKey: ['bot', 'conversations'] })
    },
  })
}

// ── Turns / answers ──────────────────────────────────────────────────────────

/**
 * Polls a single turn by id. `poll` (default true) gates the interval, not
 * the initial fetch — a turn is always fetched once so its `conversationId`
 * is known. Callers (BotWidget's <TurnWatcher/>) pass `poll: false` once
 * they've learned this turn belongs to the conversation BotPanel is already
 * polling via `useBotConversation`, to avoid hitting GET /turns/{id} twice
 * a second for the same data.
 */
export function useBotTurn(turnId: number | null, opts?: { poll?: boolean }) {
  const poll = opts?.poll ?? true
  return useQuery({
    queryKey: ['bot', 'turn', turnId],
    queryFn: () => getTurn(turnId as number),
    enabled: turnId != null,
    refetchInterval: (query) => {
      if (!poll) return false
      if (query.state.status === 'error') return false
      const data = query.state.data as BotTurn | undefined
      return anyNonTerminal(data) ? 1500 : false
    },
    refetchIntervalInBackground: false,
  })
}

export function useAskTurn() {
  const qc = useQueryClient()
  const setConversation = useBotStore((s) => s.setConversation)
  const addPending = useBotStore((s) => s.addPending)
  const setViewMode = useBotStore((s) => s.setViewMode)
  return useMutation({
    mutationFn: (body: TurnCreateRequest) => askTurn(body),
    onSuccess: (res) => {
      setConversation(res.conversationId)
      addPending(res.turnId)
      // O5/O6: asking with 2-3 models switches to the compare dock. Driven
      // directly off this response's answerIds (one per resolved model,
      // returned synchronously by POST /turns) rather than diffing the
      // polled conversation's turn count — that heuristic misses a brand
      // new conversation's very first turn (its "previous count" is
      // indistinguishable from an ordinary conversation switch/history
      // load, so it never fires for exactly the most common multi-model
      // path: new conversation, 3 models, first question).
      if (res.answerIds.length >= 2) setViewMode('dock')
      qc.invalidateQueries({ queryKey: ['bot', 'conversation', res.conversationId] })
      // A brand-new conversation (no conversationId passed in) has no
      // existing list entry, and an existing one's updatedAt/cost is now
      // stale — either way the conversation-switcher Dropdown needs it.
      qc.invalidateQueries({ queryKey: ['bot', 'conversations'] })
      // A queued answer soon changes both spend aggregates (the org total
      // once the worker records bot.Usage, the caller's own turn already
      // counts here) — invalidate eagerly rather than waiting for the
      // panel's 60s/30s staleTime, same reasoning as TurnWatcher's
      // completion-path invalidation in BotWidget.tsx.
      qc.invalidateQueries({ queryKey: ['bot', 'usage', 'month'] })
      qc.invalidateQueries({ queryKey: ['bot', 'usage', 'report'] })
    },
  })
}

export function useCancelAnswer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (answerId: number) => cancelAnswer(answerId),
    onSuccess: () => {
      // The exact conversation/turn owning this answer isn't known here —
      // invalidate both broad prefixes so the next render reflects the
      // cancellation without waiting for the next poll tick.
      qc.invalidateQueries({ queryKey: ['bot', 'conversation'] })
      qc.invalidateQueries({ queryKey: ['bot', 'turn'] })
    },
  })
}

// ── Memory ───────────────────────────────────────────────────────────────────

export function useBotMemory() {
  return useQuery({
    queryKey: ['bot', 'memory'],
    queryFn: getMemory,
  })
}

export function useDeleteMemory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => deleteMemory(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bot', 'memory'] }),
  })
}

// ── Settings ─────────────────────────────────────────────────────────────────

export function useBotSettings(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['bot', 'settings'],
    queryFn: getSettings,
    enabled: opts?.enabled ?? true,
  })
}

export function usePatchSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: SettingsPatchRequest) => patchSettings(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bot', 'settings'] }),
  })
}

/**
 * IT.Admin-only org-wide guardrails/knowledge text (migration 268's
 * bot.OrgSetting). `enabled` mirrors useModelCatalog's pattern above — the
 * caller (SettingsTab) passes `isAdmin` so the request never fires for a
 * non-admin.
 */
export function useOrgSettings(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['bot', 'org-settings'],
    queryFn: getOrgSettings,
    staleTime: 30_000,
    enabled: opts?.enabled ?? true,
  })
}

export function usePatchOrgSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: OrgSettingsPatchRequest) => patchOrgSettings(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bot', 'org-settings'] }),
  })
}

/**
 * One-time seed of the live `viewMode` from the persisted server-side
 * `compareLayout` setting (Contract H fix) — without this, `compareLayout`
 * is written by SettingsTab and displayed right back from the same GET, but
 * nothing ever reads it into `botStore.viewMode`, so it has no effect on a
 * fresh browser/device (which only ever sees `viewMode`'s hardcoded
 * 'popover' default). Gated on `viewModeSeeded` both here (`enabled`, so the
 * request stops firing forever once seeded) and inside `seedViewMode`
 * itself (so an explicit `setViewMode` that lands first — e.g. the user
 * clicks "Vergleich" before this fetch resolves — always wins).
 */
export function useSeedViewMode(enabled: boolean) {
  const viewModeSeeded = useBotStore((s) => s.viewModeSeeded)
  const seedViewMode = useBotStore((s) => s.seedViewMode)
  const { data } = useBotSettings({ enabled: enabled && !viewModeSeeded })

  useEffect(() => {
    if (!data || viewModeSeeded) return
    seedViewMode(data.compareLayout === 'dock' ? 'dock' : 'popover')
  }, [data, viewModeSeeded, seedViewMode])
}

// ── Usage ────────────────────────────────────────────────────────────────────

export function useMonthUsage(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['bot', 'usage', 'month'],
    queryFn: getMonthUsage,
    staleTime: 60 * 1000,
    enabled: opts?.enabled ?? true,
  })
}

/** GET /api/bot/usage/report — ReportTab's data source (per-provider cost
 *  breakdown). See useMonthUsage above for the sibling single-total query;
 *  both are invalidated together (useAskTurn's onSuccess, TurnWatcher's
 *  completion path in BotWidget.tsx) since a queued/finished answer changes
 *  both at once. */
export function useUsageReport(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['bot', 'usage', 'report'],
    queryFn: getUsageReport,
    staleTime: 30 * 1000,
    enabled: opts?.enabled ?? true,
  })
}

// ── Startup reconciliation ───────────────────────────────────────────────────

/**
 * `pendingTurnIds` is deliberately excluded from botStore's persisted slice
 * (in-flight tracking must reflect the server, not a stale localStorage
 * snapshot) — but that means a reload while an answer is still non-terminal
 * drops it silently: no <TurnWatcher/> is ever mounted for it again, so its
 * eventual completion never bumps the unread badge or fires the toast.
 *
 * `activeConversationId` IS persisted, so on BotWidget mount we re-fetch
 * just that one conversation (regardless of panel open/closed state) and
 * re-add any turn that's still non-terminal. `addPending` already dedupes,
 * so this is a harmless no-op once the normal flow has already registered a
 * turn. Scoped to the active conversation rather than every conversation
 * the user has ever had, since that's the only one pendingTurnIds could
 * plausibly have referenced when the page unloaded — a full org-wide sweep
 * would need a backend endpoint this module doesn't have.
 */
export function useReconcilePendingTurns(conversationId: number | null) {
  const addPending = useBotStore((s) => s.addPending)
  const { data } = useQuery({
    queryKey: ['bot', 'conversation', conversationId],
    queryFn: () => getConversation(conversationId as number),
    enabled: conversationId != null,
    staleTime: 0,
  })

  useEffect(() => {
    if (!data) return
    for (const turn of data.turns) {
      if (anyNonTerminal(turn)) addPending(turn.turnId)
    }
  }, [data, addPending])
}
